import { useState, useRef } from 'react';
import { Client, Vacancy, CopyVariant, Visual } from '../../consultantTypes';
import { CopyVariantCard } from '../components/CopyVariantCard';
import { VisualItemCard } from '../components/VisualItemCard';
import { ComposerSidebar } from '../components/ComposerSidebar';
import { PreviewModal } from '../components/PreviewModal';
import { FacebookCampaignSetup, CampaignData } from '../components/FacebookCampaignSetup';
import { ArrowLeft, Upload, Eye, Facebook, Link2, Check, Plus } from 'lucide-react';

interface ComposerProps {
  client: Client;
  vacancy: Vacancy;
  allVacancies: Vacancy[];
  copyVariants: CopyVariant[];
  visuals: Visual[];
  allCopyVariants: CopyVariant[];
  allVisuals: Visual[];
  onBack: () => void;
  onAddManualCopy: (vacancyId: string, title: string, body: string) => void;
  onToggleSelect: (id: string) => void;
  onToggleVisualSelect: (id: string) => void;
  onEditCopy: (id: string, body: string) => void;
  onUploadVisual: (vacancyId: string, file: File) => void;
  onUploadNewVersion: (visualId: string, file: File, resolvedFeedbackIds: string[]) => Promise<void>;
  onShare: (vacancyId: string) => void;
  onSwitchVacancy: (vacancyId: string) => void;
}

export function Composer({
  client,
  vacancy,
  allVacancies,
  copyVariants,
  visuals,
  allCopyVariants,
  allVisuals,
  onBack,
  onAddManualCopy,
  onToggleSelect,
  onToggleVisualSelect,
  onEditCopy,
  onUploadVisual,
  onUploadNewVersion,
  onShare,
  onSwitchVacancy
}: ComposerProps) {

  const getVacancyStatus = (vacancyToCheck: Vacancy) => {
    if (!vacancyToCheck.shared) {
      return 'draft';
    }

    if (!vacancyToCheck.reviewCompletedAt) {
      return 'pending';
    }

    const vacancyCopyVariants = allCopyVariants.filter(cv => cv.vacancyId === vacancyToCheck.id && cv.selected);
    const vacancyVisuals = allVisuals.filter(v => v.vacancyId === vacancyToCheck.id && v.selected);

    const allCopyApproved = vacancyCopyVariants.length > 0 && vacancyCopyVariants.every(cv => cv.status === 'approved');
    const allVisualsApproved = vacancyVisuals.length > 0 && vacancyVisuals.every(v => v.status === 'APPROVED');

    if (allCopyApproved && allVisualsApproved) {
      return 'approved';
    }

    return 'feedback';
  };
  const [showPreview, setShowPreview] = useState(false);
  const [showFacebookSetup, setShowFacebookSetup] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [newCopyText, setNewCopyText] = useState('');

  const handleAddCopyVariant = () => {
    const text = newCopyText.trim();
    if (!text) return;

    const variantLabel = String.fromCharCode(65 + copyVariants.length);
    onAddManualCopy(vacancy.id, `Variant ${variantLabel}`, text);
    setNewCopyText('');
  };

  const copyShareLink = async () => {
    if (!vacancy.shareToken) return;

    const shareUrl = `${window.location.origin}/client?share=${vacancy.shareToken}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('Kon link niet kopiëren. Probeer het opnieuw.');
    }
  };

  const selectedCount = copyVariants.filter(c => c.selected).length;
  const selectedVisualsCount = visuals.filter(v => v.selected).length;
  const canShare = selectedCount >= 3 && selectedCount <= 4 && selectedVisualsCount >= 3 && selectedVisualsCount <= 4;

  const approvedCopyCount = copyVariants.filter(c => c.selected && c.status === 'approved').length;
  const approvedVisualsCount = visuals.filter(v => v.selected && v.status === 'APPROVED').length;
  const canCreateAds = approvedCopyCount >= 3 && approvedVisualsCount >= 3;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        onUploadVisual(vacancy.id, file);
      });
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleShare = () => {
    onShare(vacancy.id);
    setShowPreview(false);
  };

  const handleFacebookCampaignComplete = (campaignData: CampaignData) => {
    console.log('Creating Facebook Campaign with data:', campaignData);
    console.log('Campaign will include:', {
      visuals: approvedVisuals.length,
      copyVariants: approvedCopyCount,
      totalAds: approvedVisuals.length * approvedCopyCount
    });

    alert(`Campagne "${campaignData.campaignName}" succesvol aangemaakt!\n\n` +
      `• ${approvedVisuals.length} visuals\n` +
      `• ${approvedCopyCount} tekstvarianten\n` +
      `• ${approvedVisuals.length * approvedCopyCount} advertenties\n` +
      `• Locatie: ${campaignData.location}\n\n` +
      `De campagne is nu actief in Facebook Ads Manager.`);

    setShowFacebookSetup(false);
  };

  const approvedCopy = copyVariants.filter(c => c.selected && c.status === 'approved');
  const approvedVisualsFiltered = visuals.filter(v => v.selected && v.status === 'APPROVED');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <img
            src="/NEEDLES Logo (Rechthoekig).svg"
            alt="Needles Logo"
            className="h-8 mb-4"
          />
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Terug naar Dashboard</span>
            </button>

            <div className="flex items-center gap-3">
              {vacancy.shared && vacancy.shareToken && (
                <button
                  onClick={copyShareLink}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    copiedLink
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Kopieer link voor klant"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4" />
                      Gekopieerd!
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4" />
                      Kopieer Link
                    </>
                  )}
                </button>
              )}
              {canCreateAds && (
                <button
                  onClick={() => setShowFacebookSetup(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                  Zet campagne op
                </button>
              )}
              <button
                onClick={() => setShowPreview(true)}
                disabled={!canShare}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Eye className="w-4 h-4" />
                Klaar om te delen
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <span className="font-medium">Alle vacatures voor {client.name}:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {allVacancies.filter(v => v.clientId === client.id).map(v => {
                const status = getVacancyStatus(v);
                const isActive = v.id === vacancy.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => onSwitchVacancy(v.id)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : status === 'feedback'
                        ? 'bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100'
                        : status === 'approved'
                        ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                        : status === 'draft'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {v.title}
                    {status === 'feedback' && !isActive && ' ⚠️'}
                    {status === 'approved' && !isActive && ' ✓'}
                    {status === 'draft' && !isActive && ' 📝'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Vacature & Copy</h2>

              <div className="bg-white rounded-2xl shadow-md p-8 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Vacaturetekst</h3>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {vacancy.vacancyText}
                  </p>
                </div>
                {vacancy.notes && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Interne notities</h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{vacancy.notes}</p>
                  </div>
                )}
              </div>

              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Tekstvarianten toevoegen</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Plak je tekst hieronder en klik op "Toevoegen". Je kunt maximaal 5 varianten toevoegen.
                  </p>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                    <textarea
                      value={newCopyText}
                      onChange={(e) => setNewCopyText(e.target.value)}
                      placeholder="Plak hier je tekst..."
                      disabled={copyVariants.length >= 5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                      rows={5}
                    />
                    <button
                      onClick={handleAddCopyVariant}
                      disabled={!newCopyText.trim() || copyVariants.length >= 5}
                      className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Toevoegen
                    </button>
                  </div>
                </div>

                {copyVariants.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Opgeslagen varianten ({copyVariants.length}/5)</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {copyVariants.map(variant => (
                        <CopyVariantCard
                          key={variant.id}
                          variant={variant}
                          onToggleSelect={onToggleSelect}
                          onEdit={onEditCopy}
                          disabled={selectedCount >= 4 && !variant.selected}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Visuals</h2>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Upload Images
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {visuals.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {visuals.map(visual => (
                    <VisualItemCard
                      key={visual.id}
                      visual={visual}
                      onToggleSelect={onToggleVisualSelect}
                      disabled={selectedVisualsCount >= 4 && !visual.selected}
                      onUploadNewVersion={async (file, resolvedFeedbackIds) => {
                        await onUploadNewVersion(visual.id, file, resolvedFeedbackIds);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    Klik op "Upload Images" om visuals toe te voegen
                  </p>
                </div>
              )}
            </section>
          </div>

          <div className="lg:col-span-1">
            <ComposerSidebar
              copyVariants={copyVariants}
              visuals={visuals}
              canShare={canShare}
            />
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal
          copyVariants={copyVariants}
          visuals={visuals}
          onClose={() => setShowPreview(false)}
          onConfirm={handleShare}
        />
      )}

      {showFacebookSetup && (
        <FacebookCampaignSetup
          client={client}
          vacancy={vacancy}
          approvedCopyVariants={approvedCopy}
          approvedVisuals={approvedVisualsFiltered}
          onClose={() => setShowFacebookSetup(false)}
          onComplete={handleFacebookCampaignComplete}
        />
      )}
    </div>
  );
}
